import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { Icon } from './icon';
import { ChevronRight, Home } from 'lucide-react';

const routeLabels = {
  '': 'Dashboard',
  'dashboard': 'Dashboard',
  'users': 'Users',
  'analytics': 'Analytics',
  'products': 'Products',
  'orders': 'Orders',
  'messages': 'Chat',
  'calendar': 'Calendar',
  'database': 'Database',
  'settings': 'Settings',
  'ui-components': 'UI Components',
  'profile': 'Profile',
  'tables': 'Tables',
  'forms': 'Forms',
  'components': 'Components',
  'accordion': 'Accordion',
  'alert': 'Alert',
  'alert-dialog': 'Alert Dialog',
  'aspect-ratio': 'Aspect Ratio',
  'avatar': 'Avatar',
  'badge': 'Badge',
  'breadcrumb': 'Breadcrumb',
  'button': 'Button',
  'checkbox': 'Checkbox',
  'collapsible': 'Collapsible',
  'combobox': 'Combobox',
  'command': 'Command',
  'dialog': 'Dialog',
  'dropdown-menu': 'Dropdown Menu',
  'pagination': 'Pagination',
  'popover': 'Popover',
  'progress': 'Progress',
  'radio-group': 'Radio Group',
  'select': 'Select',
  'separator': 'Separator',
  'sheet': 'Sheet',
  'skeleton': 'Skeleton',
  'slider': 'Slider',
  'switch': 'Switch',
  'tabs': 'Tabs',
  'textarea': 'Textarea',
  'toggle': 'Toggle',
  'tooltip': 'Tooltip',
  'typography': 'Typography',
  'hrms': 'HRMS',
  'jobs': 'Job Portal',
  'starter': 'Starter Page',
};

const formatLabel = (segment) => {
  if (routeLabels[segment.toLowerCase()]) {
    return routeLabels[segment.toLowerCase()];
  }
  // Fallback: capitalize words and replace dashes with spaces
  return segment
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const PageBreadcrumb = ({ className }) => {
  const location = useLocation();
  const pathname = location.pathname;

  // Split and filter out empty segments
  const segments = pathname.split('/').filter(Boolean);

  // If we are on the home/dashboard page itself, show a simple Home indicator
  const isHome = segments.length === 0 || (segments.length === 1 && segments[0] === 'dashboard');

  return (
    <nav
      aria-label="Breadcrumb"
      className={cn(
        "flex items-center text-xs font-medium text-muted-foreground select-none transition-all duration-300",
        className
      )}
    >
      <ol className="flex items-center gap-1.5 sm:gap-2 overflow-x-auto whitespace-nowrap scrollbar-none max-w-full">
        {/* Home Item */}
        <li className="flex items-center">
          <Link
            to="/"
            className="flex items-center gap-1 hover:text-foreground transition-colors duration-200"
          >
            <Icon icon={Home} color="muted" size="xs" />
            <span className="hidden sm:inline">Home</span>
          </Link>
        </li>

        {/* Dynamic Items */}
        {segments.map((segment, index) => {
          let url = `/${segments.slice(0, index + 1).join('/')}`;
          if (url === '/components') {
            url = '/ui-components';
          }
          const isLast = index === segments.length - 1;
          const label = formatLabel(segment);

          // Skip rendering 'components' segment if it's the only one (unlikely but safe)
          if (segment === 'components' && segments.length === 1) return null;

          return (
            <React.Fragment key={url}>
              {/* Separator */}
              <li role="presentation" aria-hidden="true" className="text-muted-foreground/40">
                <Icon icon={ChevronRight} color="muted" size="xs" />
              </li>

              {/* Breadcrumb Item */}
              <li className="flex items-center">
                {isLast ? (
                  <span className="font-semibold text-foreground truncate max-w-[150px] sm:max-w-none">
                    {label}
                  </span>
                ) : (
                  <Link
                    to={url}
                    className="hover:text-foreground transition-colors duration-200 truncate max-w-[120px] sm:max-w-none"
                  >
                    {label}
                  </Link>
                )}
              </li>
            </React.Fragment>
          );
        })}
      </ol>
    </nav>
  );
};

export default PageBreadcrumb;
